import { useEffect, useState } from "react";
import type { SearchMode } from "../../shared/types";
import { isCycle, isPlanSlug, type BillingCycle, type PlanSlug } from "../../shared/plans";

export type AccountTab = "plano" | "monitoramento" | "chaves" | "seguranca";
const ACCOUNT_TABS: AccountTab[] = ["plano", "monitoramento", "chaves", "seguranca"];

export type Route =
  | { page: "home"; mode?: SearchMode; q?: string }
  | { page: "company"; cnpj: string }
  | { page: "network"; cnpj: string }
  | { page: "person"; name: string; doc?: string; de?: string }
  | { page: "connect" }
  | { page: "guide" }
  | { page: "studio" }
  | { page: "landing"; anchor?: "planos" }
  | { page: "login"; volta?: string }
  | { page: "signup"; plano?: PlanSlug; ciclo?: BillingCycle; volta?: string }
  | { page: "forgot" }
  | { page: "reset"; token: string }
  | { page: "account"; tab: AccountTab; pagamento?: string; assinar?: PlanSlug; ciclo?: BillingCycle }
  | { page: "batch" }
  | { page: "legal"; doc: "termos" | "privacidade" }
  | { page: "notFound"; path: string };

// "cpf" saiu da interface; links antigos com m=cpf caem na busca por sócio.
const MODES: SearchMode[] = ["auto", "cnpj", "company", "partner"];

/**
 * O build do GitHub Pages não tem servidor para responder caminhos limpos,
 * então lá a navegação continua por hash. No site com servidor as URLs são
 * de verdade — sem isso o Google indexa uma página só.
 */
// O `typeof` é o que deixa este módulo ser importado pelos testes: `import.meta.env`
// só existe dentro do bundle do Vite, e em Node ler `.VITE_STATIC` dele quebrava.
// O acesso literal continua ali para o Vite substituir na hora do build.
const HASH_ONLY = typeof import.meta.env !== "undefined" && import.meta.env.VITE_STATIC === "1";

/**
 * Destino de volta depois de entrar. Só caminho relativo do próprio site: "//outro",
 * "/\outro" (barra invertida) e caracteres de controle o navegador lê como outro domínio.
 */
export function safeReturnPath(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  if (!value.startsWith("/") || value.startsWith("//") || /[\\\u0000-\u001f\u007f]/.test(value)) return undefined;
  return value.slice(0, 300);
}

export function parseRoutePath(path: string, queryString: string): Route {
  const [section, value] = path.replace(/^\/+/, "").split("/");
  const cnpj = (value ?? "").replace(/\D/g, "");
  if (section === "empresa" && cnpj.length === 14) return { page: "company", cnpj };
  if (section === "rede" && cnpj.length === 14) return { page: "network", cnpj };
  if (section === "pessoa" && value) {
    const params = new URLSearchParams(queryString);
    return {
      page: "person",
      name: decodeURIComponent(value),
      doc: params.get("doc") ?? undefined,
      de: params.get("de") ?? undefined,
    };
  }
  if (section === "conectar-ia" || section === "mcp") return { page: "connect" };
  if (section === "consulta-cnpj-gratis" || section === "guia") return { page: "guide" };
  if (section === "studio" || section === "estudio") return { page: "studio" };

  const params = new URLSearchParams(queryString);
  if (section === "conheca") return { page: "landing" };
  if (section === "planos") return { page: "landing", anchor: "planos" };
  if (section === "entrar") return { page: "login", volta: safeReturnPath(params.get("volta")) };
  if (section === "criar-conta") {
    const plano = params.get("plano");
    const ciclo = params.get("ciclo");
    return {
      page: "signup",
      plano: isPlanSlug(plano) ? plano : undefined,
      ciclo: isCycle(ciclo) ? ciclo : undefined,
      volta: safeReturnPath(params.get("volta")),
    };
  }
  if (section === "recuperar-senha") return { page: "forgot" };
  if (section === "redefinir-senha") return { page: "reset", token: params.get("token") ?? "" };
  if (section === "conta") {
    const tab = (ACCOUNT_TABS as string[]).includes(value ?? "") ? (value as AccountTab) : value ? null : "plano";
    if (tab) {
      const assinar = params.get("assinar");
      const ciclo = params.get("ciclo");
      return {
        page: "account",
        tab,
        pagamento: params.get("pagamento") ?? undefined,
        assinar: isPlanSlug(assinar) ? assinar : undefined,
        ciclo: isCycle(ciclo) ? ciclo : undefined,
      };
    }
  }
  if (section === "lote" && !value) return { page: "batch" };
  if (section === "termos" && !value) return { page: "legal", doc: "termos" };
  if (section === "privacidade" && !value) return { page: "legal", doc: "privacidade" };

  // Só a raiz é a home. Antes qualquer caminho desconhecido caía aqui: quem
  // errava a URL recebia a busca, com status 200, como se o endereço existisse.
  if (section) return { page: "notFound", path: `/${path.replace(/^\/+/, "")}` };

  const rawMode = params.get("m") as SearchMode | null;
  const mode = rawMode === "cpf" ? "partner" : rawMode;
  return {
    page: "home",
    mode: mode && MODES.includes(mode) ? mode : undefined,
    q: params.get("q") ?? undefined,
  };
}

function parseLocation(): Route {
  // Links antigos (#/empresa/...) continuam funcionando e têm prioridade. Só o hash que
  // começa com "#/" é rota: "#secao" é âncora dentro da página (termos, privacidade).
  const hash = window.location.hash.startsWith("#/") ? window.location.hash.slice(2) : "";
  if (hash) {
    const [path, queryString] = hash.split("?");
    return parseRoutePath(path, queryString ?? "");
  }
  return parseRoutePath(window.location.pathname, window.location.search.replace(/^\?/, ""));
}

/** Caminho canônico de uma rota, o mesmo que o servidor entende e o Google indexa. */
export function routePath(route: Route): string {
  if (route.page === "company") return `/empresa/${route.cnpj}`;
  if (route.page === "network") return `/rede/${route.cnpj}`;
  if (route.page === "person") {
    const params = new URLSearchParams();
    if (route.doc) params.set("doc", route.doc);
    // a empresa de onde o clique veio segue na URL: é o que permite montar o
    // perfil de quem a busca por nome não indexa, inclusive num link colado
    if (route.de) params.set("de", route.de);
    const suffix = params.toString() ? `?${params.toString()}` : "";
    return `/pessoa/${encodeURIComponent(route.name)}${suffix}`;
  }
  if (route.page === "connect") return "/conectar-ia";
  if (route.page === "guide") return "/consulta-cnpj-gratis";
  if (route.page === "studio") return "/estudio";
  if (route.page === "landing") return route.anchor === "planos" ? "/planos" : "/conheca";
  if (route.page === "login") return route.volta ? `/entrar?volta=${encodeURIComponent(route.volta)}` : "/entrar";
  if (route.page === "signup") {
    const params = new URLSearchParams();
    if (route.plano) params.set("plano", route.plano);
    if (route.ciclo) params.set("ciclo", route.ciclo);
    if (route.volta) params.set("volta", route.volta);
    const suffix = params.toString() ? `?${params.toString()}` : "";
    return `/criar-conta${suffix}`;
  }
  if (route.page === "forgot") return "/recuperar-senha";
  if (route.page === "reset") return `/redefinir-senha?token=${encodeURIComponent(route.token)}`;
  if (route.page === "account") {
    const params = new URLSearchParams();
    if (route.pagamento) params.set("pagamento", route.pagamento);
    if (route.assinar) params.set("assinar", route.assinar);
    if (route.ciclo) params.set("ciclo", route.ciclo);
    const suffix = params.toString() ? `?${params.toString()}` : "";
    return `${route.tab === "plano" ? "/conta" : `/conta/${route.tab}`}${suffix}`;
  }
  if (route.page === "batch") return "/lote";
  if (route.page === "legal") return `/${route.doc}`;
  if (route.page === "notFound") return route.path;
  if (route.q) {
    const params = new URLSearchParams();
    params.set("m", route.mode ?? "auto");
    params.set("q", route.q);
    return `/?${params.toString()}`;
  }
  return "/";
}

function href(route: Route): string {
  const path = routePath(route);
  return HASH_ONLY ? `#${path}` : path;
}

const ROUTE_EVENT = "teia:route";

export function navigate(route: Route): void {
  const target = href(route);
  if (HASH_ONLY) {
    window.location.hash = target;
    return;
  }
  // Sai do modo hash quando o link veio de um compartilhamento antigo.
  history.pushState(null, "", target);
  window.dispatchEvent(new Event(ROUTE_EVENT));
  window.scrollTo({ top: 0 });
}

/** Atualiza a URL da busca sem disparar navegação (histórico compartilhável). */
export function reflectSearch(mode: SearchMode, q: string): void {
  history.replaceState(null, "", href({ page: "home", mode, q: q || undefined }));
}

export function useHashRoute(): Route {
  const [route, setRoute] = useState<Route>(parseLocation);
  useEffect(() => {
    const onChange = () => setRoute(parseLocation());
    window.addEventListener("hashchange", onChange);
    window.addEventListener("popstate", onChange);
    window.addEventListener(ROUTE_EVENT, onChange);
    return () => {
      window.removeEventListener("hashchange", onChange);
      window.removeEventListener("popstate", onChange);
      window.removeEventListener(ROUTE_EVENT, onChange);
    };
  }, []);
  return route;
}
